import { parseBounds } from "@/bounds";
import { mixedInspections, sharedInspectionEpsg, startCrsInspection } from "@/import-inspection";
import { MapPreview, useMapPreviewBounds, type Bounds2D } from "@/components/MapPreview";
import { useProjectionName } from "@/components/ProjectionName";
import { normalizeInputProjection, resolveProj4Projection, validateInputProjection } from "@/projection";
import {
    cancelUploads,
    retryFailedUploads,
    UploadManager,
    uploadsReady,
    type Upload as FileUpload,
    type UploadFetch,
} from "@/upload";
import { getEnvVar } from "@/utils";
import { ClerkProvider, useClerk } from "@clerk/react";
import { IconBolt, IconCirclePlus, IconCircleX, IconFile3d, IconInfoCircle } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { proxy, ref, useSnapshot } from "valtio";
import lumiLogo from "../../assets/lumidb_logo_color.svg";

type UploadSession = { request: UploadFetch; signOut: () => Promise<void> };
type CreateUploadSession = (name: string) => Promise<UploadSession>;

// crypto.randomUUID is secure-context-only; getRandomValues also works over plain HTTP (LAN dev/staging).
function randomHex(bytes: number): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (byte) => byte.toString(16).padStart(2, "0")).join(
        "",
    );
}

function generateUploadName(): string {
    const adjectives = [
        "Bright",
        "Calm",
        "Clear",
        "Clever",
        "Curious",
        "Gentle",
        "Golden",
        "Happy",
        "Kind",
        "Lively",
        "Lucky",
        "Merry",
        "Misty",
        "Quiet",
        "Sunny",
        "Swift",
    ];
    const nouns = [
        "Badger",
        "Bear",
        "Falcon",
        "Finch",
        "Fox",
        "Heron",
        "Lynx",
        "Otter",
        "Owl",
        "Panda",
        "Robin",
        "Seal",
        "Sparrow",
        "Tern",
        "Whale",
        "Wolf",
    ];
    const bytes = crypto.getRandomValues(new Uint8Array(5));
    const suffix = Array.from(bytes.slice(2), (byte) => byte.toString(16).padStart(2, "0"))
        .join("")
        .toUpperCase();
    return `${adjectives[bytes[0] & 0x0f]} ${nouns[bytes[1] & 0x0f]} ${suffix}`;
}

async function checkResponse(response: Response): Promise<void> {
    if (!response.ok) {
        const error: unknown = await response.json().catch(() => null);
        const detail = error && typeof error === "object" ? error : {};
        const reason = "reason" in detail ? detail.reason : undefined;
        const status = "status_text" in detail ? detail.status_text : undefined;
        throw new Error(
            typeof reason === "string"
                ? reason
                : typeof status === "string"
                  ? status
                  : `Request failed (${response.status})`,
        );
    }
}

async function responseJson<T>(response: Response): Promise<T> {
    await checkResponse(response);
    return response.json() as Promise<T>;
}

function Session({ children }: { children: (create: CreateUploadSession) => ReactNode }) {
    const clerk = useClerk();
    const uploadSessionId = useRef<string | null>(null);
    const signOut = useCallback(async () => {
        const sessionId = uploadSessionId.current;
        if (!sessionId) return;
        await clerk.signOut(() => {}, { sessionId });
        if (uploadSessionId.current === sessionId) uploadSessionId.current = null;
    }, [clerk]);

    useEffect(() => {
        const handlePageHide = (event: PageTransitionEvent) => {
            if (!event.persisted) {
                void signOut().catch((error) => console.warn("Unable to sign out upload session", error));
            }
        };
        window.addEventListener("pagehide", handlePageHide);
        return () => window.removeEventListener("pagehide", handlePageHide);
    }, [signOut]);

    async function createSession(name: string): Promise<UploadSession> {
        if (!clerk.loaded) throw new Error("Secure upload session is still loading. Please retry in a moment.");

        if (!clerk.session) {
            const anonymous = await responseJson<{ organization_slug: string; sign_in_url: string }>(
                await fetch("/api/anonymous", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name }),
                    signal: AbortSignal.timeout(60_000),
                }),
            );
            const ticket = new URL(anonymous.sign_in_url).searchParams.get("__clerk_ticket");
            if (!ticket) throw new Error("Could not start the anonymous upload session. Please retry.");
            const result = await clerk.client.signIn.create({ strategy: "ticket", ticket });
            if (!result.createdSessionId) throw new Error("Could not sign in to the anonymous upload session.");
            await clerk.setActive({
                session: result.createdSessionId,
                organization: anonymous.organization_slug,
                navigate: async () => {},
            });
        }

        // Reuse an existing session without signing the visitor out or changing their organization.
        let organizationId = clerk.organization?.id ?? clerk.session?.lastActiveOrganizationId;
        if (!organizationId && clerk.user) {
            const memberships = await clerk.user.getOrganizationMemberships();
            organizationId = memberships.data[0]?.organization.id;
            if (organizationId) await clerk.setActive({ organization: organizationId, navigate: async () => {} });
        }
        const session = clerk.session;
        if (!session || !organizationId) throw new Error("No upload organization is available for this session.");

        uploadSessionId.current = session.id;
        return {
            signOut,
            request: async (url, options) => {
                if (new URL(url, window.location.origin).origin !== window.location.origin) {
                    throw new Error("Upload API requests must be same-origin.");
                }
                const token = await session.getToken({ organizationId });
                if (!token) throw new Error("Your upload session has expired. Please reload and try again.");
                const headers = new Headers(options?.headers);
                headers.set("Authorization", `Clerk ${token}`);
                return fetch(url, { ...options, headers });
            },
        };
    }

    return children(createSession);
}

const previewCenter: [number, number] = [15, 49];
const ACCEPT = ".laz,.las,.e57";

// `name` and `email` live outside this so resetFile keeps them: the generated name must stay
// consistent with the anonymous org it created, and the visitor shouldn't retype their email.
function initialFileState() {
    return {
        files: [] as File[],
        uploads: [] as FileUpload[],
        bounds: [] as (Bounds2D | null)[],
        proj: "",
        projectionEdited: false,
        preparing: false,
        submitting: false,
        error: "",
        phase: "",
        processingProgress: 0,
        projectId: "",
        versionId: "",
        imported: false,
        invited: false,
    };
}

type ImportStatus = { status: string; phase?: string; progress?: number; error_msg?: string };
const errorMessage = (error: unknown) => {
    console.error("Upload failed", error);
    const message = error instanceof Error ? error.message.trim() : "";
    return message || "Something went wrong. Please retry.";
};

// Session, uploads and import state belong to this page, not to the authenticated app's services.
export function UploadPage({ createSession }: { createSession: CreateUploadSession }) {
    const [state] = useState(() => proxy({ ...initialFileState(), name: generateUploadName(), email: "" }));
    const input = useSnapshot(state);
    const fileInput = useRef<HTMLInputElement>(null);
    const operation = useRef(new AbortController());
    const sessionPromise = useRef<Promise<UploadSession> | null>(null);
    const [dragging, setDragging] = useState(false);
    const [validEmail, setValidEmail] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const crsRequest = useCallback<UploadFetch>(async (url, options) => {
        const session = await getSession();
        return session.request(url, options);
    }, []);
    const projectionName = useProjectionName(input.proj, crsRequest);
    const bounds = useMapPreviewBounds(
        input.bounds.map((bounds, index) => ({
            proj: input.proj,
            bounds,
            state: input.uploads[index]?.status.kind ?? "queued",
        })),
    );
    const validProjection = validateInputProjection(normalizeInputProjection(input.proj));
    const uploaded = uploadsReady(input.uploads);
    const failed = input.uploads.some((upload) => upload.status.kind === "failed");
    const showProgressStripes =
        input.uploads.some((upload) => upload.status.kind === "uploading") || (input.submitting && uploaded);
    const totalSize = input.files.reduce((sum, file) => sum + file.size, 0);
    const uploadProgress = totalSize
        ? input.uploads.reduce((sum, upload) => sum + upload.progress * upload.file.size, 0) / totalSize
        : 0;

    useEffect(() => {
        return () => {
            operation.current.abort();
            cancelUploads(state.uploads);
        };
    }, [state]);

    function getSession() {
        if (!sessionPromise.current) {
            sessionPromise.current = createSession(state.name).catch((error) => {
                sessionPromise.current = null;
                throw error;
            });
        }
        return sessionPromise.current;
    }

    function resetFile() {
        operation.current.abort();
        operation.current = new AbortController();
        cancelUploads(state.uploads);
        Object.assign(state, initialFileState());
        setSubmitAttempted(false);
        if (fileInput.current) fileInput.current.value = "";
    }

    function updateDetectedProjection() {
        if (!state.projectionEdited && !state.submitting && !state.versionId) {
            state.proj = sharedInspectionEpsg(state.uploads) ?? "";
        }
    }

    async function prepareUploads(signal: AbortSignal) {
        state.preparing = true;
        try {
            const session = await getSession();
            if (signal.aborted) return;
            if (!state.uploads.length) {
                state.uploads = state.files.map((file) => UploadManager.startFile(file, session.request));
            }
            for (const upload of state.uploads) {
                startCrsInspection(upload, updateDetectedProjection, null, session.request);
            }
            updateDetectedProjection();
        } catch (error) {
            if (!signal.aborted) state.error = errorMessage(error);
        } finally {
            if (!signal.aborted) state.preparing = false;
        }
    }

    function chooseFiles(files: File[]) {
        if (state.submitting || state.invited || files.length === 0) return;
        if (files.some((file) => !/\.(laz|las|e57)$/i.test(file.name) || file.size === 0)) {
            state.error = "Choose a non-empty .laz, .las or .e57 file.";
            return;
        }
        resetFile();
        state.files = files.map((file) => ref(file));
        const signal = operation.current.signal;
        state.bounds = files.map(() => null);
        files.forEach((file, index) => {
            void parseBounds(file)
                .then((result) => {
                    if (signal.aborted || !result) return;
                    state.bounds[index] = result;
                })
                .catch(() => {});
        });
        void prepareUploads(signal);
    }

    function dropFile(event: DragEvent) {
        event.preventDefault();
        setDragging(false);
        chooseFiles(Array.from(event.dataTransfer.files));
    }

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (state.submitting || !state.files.length) return;
        // Keep the button clickable even when fields are missing: surface the errors instead.
        setSubmitAttempted(true);
        setEmailTouched(true);
        if (!validEmail || !validProjection) return;
        const projectName = state.name;
        const signal = operation.current.signal;
        const email = state.email.trim().toLowerCase();
        const projection = normalizeInputProjection(state.proj);
        state.submitting = true;
        state.error = "";
        state.email = email;
        state.phase = "Starting upload session…";
        try {
            const session = await getSession();
            signal.throwIfAborted();
            if (!state.uploads.length) {
                state.uploads = state.files.map((file) => UploadManager.startFile(file, session.request));
            } else {
                retryFailedUploads(state.uploads);
            }
            state.phase = "Uploading…";
            while (!uploadsReady(state.uploads)) {
                const failedUpload = state.uploads.find((upload) => upload.status.kind === "failed");
                if (failedUpload?.status.kind === "failed") throw new Error(failedUpload.status.message);
                await new Promise((resolve) => window.setTimeout(resolve, 200));
                signal.throwIfAborted();
            }
            const uploads = state.uploads;
            state.phase = "Processing…";
            async function request<T>(path: string, body?: unknown): Promise<T> {
                signal.throwIfAborted();
                return responseJson<T>(
                    await session.request(path, {
                        method: body === undefined ? "GET" : "POST",
                        headers: body === undefined ? undefined : { "Content-Type": "application/json" },
                        body: body === undefined ? undefined : JSON.stringify(body),
                        signal: AbortSignal.any([signal, AbortSignal.timeout(60_000)]),
                    }),
                );
            }

            if (!state.versionId) {
                await resolveProj4Projection(projection, session.request);
                signal.throwIfAborted();
                state.proj = projection;
                if (!state.projectId) {
                    const project = await request<{ project_id: string }>("/api/anonymous/projects", {
                        project_name: projectName,
                    });
                    state.projectId = project.project_id;
                }
                // Generate a fresh table name per import so a retry after a cleared version does
                // not collide with a partially-imported table of the same name in this project.
                const tableName = `upload_${randomHex(16)}`;
                const imported = await request<{ table_version: string }>("/api/tables/import", {
                    table_name: tableName,
                    table_proj: projection,
                    project_id: state.projectId,
                    user_data: { display_name: projectName },
                    // The server invites this email to the project once the import finishes.
                    invite: { email, role_name: "viewer" },
                    inputs: uploads.map((upload) => {
                        if (upload.status.kind !== "completed") throw new Error("An upload is not complete.");
                        return { id: upload.status.assetId, proj: projection };
                    }),
                });
                state.versionId = imported.table_version;
            }

            while (!state.imported) {
                const status = await request<ImportStatus>(`/api/tables/import_status/${state.versionId}`);
                if (status.status === "failed" || status.status === "cancelled") {
                    state.versionId = "";
                    throw new Error(status.error_msg ?? "Processing failed. Check the input projection and retry.");
                }
                if (status.status === "ready") {
                    state.imported = true;
                    state.processingProgress = 100;
                    break;
                }
                state.phase = status.phase ?? (status.status === "queued" ? "Queued for processing…" : "Processing…");
                state.processingProgress = Math.max(0, Math.min(100, status.progress ?? 0));
                await new Promise((resolve) => window.setTimeout(resolve, 2000));
                signal.throwIfAborted();
            }

            // The import task invites the visitor to their project by email once processing
            // finishes, so reaching "ready" means the invitation is on its way.
            state.invited = true;
            state.phase = "Ready";
            sessionPromise.current = null;
            await session.signOut();
        } catch (error) {
            if (!signal.aborted) state.error = errorMessage(error);
        } finally {
            if (!signal.aborted) state.submitting = false;
        }
    }

    const status = input.invited
        ? "Ready"
        : input.submitting
          ? input.phase
          : input.preparing
            ? "Preparing…"
            : failed
              ? "Upload failed"
              : uploaded
                ? "Uploaded"
                : input.uploads.length
                  ? `Uploading ${Math.round(uploadProgress * 100)}%`
                  : "Ready to upload";
    const progress = input.submitting && uploaded ? input.processingProgress : uploadProgress * 100;
    const buttonClass =
        "rounded-full bg-[#cf02c6] px-6 py-4 text-lg font-medium tracking-[-0.01em] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#cf02c6] disabled:cursor-not-allowed disabled:opacity-50";

    return (
        <>
            <header className="bg-white px-5 py-5 text-[#2b2947] [color-scheme:light] sm:px-6">
                <div className="mx-auto flex max-w-[1416px] flex-wrap items-center justify-between gap-5">
                    <a href="https://lumidb.com" aria-label="LumiDB home" className="flex-none">
                        <img src={lumiLogo} alt="LumiDB" className="h-5 w-auto" />
                    </a>
                    <nav
                        aria-label="Main navigation"
                        className="flex flex-wrap items-center gap-5 text-sm font-normal sm:gap-8"
                    >
                        <a href="https://lumidb.com/#case-studies" className="hover:underline">
                            Case Studies
                        </a>
                        <a href="https://lumidb.com/surveyors" className="hover:underline">
                            For Surveyors
                        </a>
                        <a
                            href="https://lumidb.com/demo"
                            className="rounded-full bg-[#2b2947] px-4 py-3 text-white hover:bg-[#403b60]"
                        >
                            Book a demo
                        </a>
                    </nav>
                </div>
            </header>
            <main className="min-h-screen bg-white px-5 pb-16 pt-16 text-[#2b2947] [color-scheme:light] sm:px-6 lg:pb-24 lg:pt-[122px]">
                <div className="mx-auto max-w-[1416px]">
                    <header className="text-center">
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-[#fdf4ff] px-2 py-1 text-xs font-medium text-[#721378]">
                            <IconBolt size={14} stroke={1.8} aria-hidden="true" />
                            SHARE INSTANTLY
                        </div>
                        <h1 className="mt-6 text-4xl font-medium leading-[1.2] tracking-[-0.03em] sm:text-5xl lg:text-[56px]">
                            Share Point Clouds Online
                        </h1>
                        <p className="mx-auto mt-10 max-w-[1000px] text-base font-medium leading-[1.5] lg:text-[18px]">
                            LumiDB lets you share point clouds, rasters, vectors and survey files at petabyte scale, in
                            one link. View, analyze and stream to other platforms.
                        </p>
                    </header>

                    <section
                        aria-labelledby="upload-heading"
                        onDragOver={(event) => {
                            event.preventDefault();
                            if (event.dataTransfer.types.includes("Files")) setDragging(true);
                        }}
                        onDragLeave={(event) => {
                            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
                        }}
                        onDrop={dropFile}
                        className={`mt-10 grid gap-5 rounded-[12px] border p-4 lg:mt-[65px] lg:grid-cols-2 ${dragging ? "border-[#cf02c6] bg-[#fdf4ff]" : "border-[#e3e3e3]"}`}
                    >
                        <input
                            ref={fileInput}
                            type="file"
                            multiple
                            accept={ACCEPT}
                            aria-label="Select point cloud files"
                            className="hidden"
                            onChange={(event) => {
                                chooseFiles(Array.from(event.currentTarget.files ?? []));
                                event.currentTarget.value = "";
                            }}
                        />
                        {input.files.length > 0 ? (
                            <form
                                onSubmit={submit}
                                className="flex min-w-0 flex-col justify-between gap-5 py-1 lg:min-h-[500px]"
                            >
                                <h2 id="upload-heading" className="sr-only">
                                    Upload your point cloud
                                </h2>
                                <div>
                                    <div className="relative flex min-w-0 items-center gap-3 overflow-hidden rounded-[12px] border border-[#e3e3e3] px-5 py-4 text-base sm:text-xl">
                                        <div
                                            className={`pointer-events-none absolute inset-y-0 left-0 transition-[width] ${showProgressStripes ? "animate-progress opacity-50" : "bg-[#f5f3f8]"}`}
                                            style={{
                                                width: `${Math.max(0, Math.min(100, progress))}%`,
                                                ...(showProgressStripes
                                                    ? {
                                                          background: "#e5e5e5",
                                                          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23474747' fill-opacity='0.25' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
                                                      }
                                                    : {}),
                                            }}
                                        />
                                        <IconFile3d
                                            size={28}
                                            stroke={1.6}
                                            className="relative flex-none text-[#aaa0b1]"
                                            aria-hidden="true"
                                        />
                                        <span
                                            className="relative min-w-0 flex-1 truncate font-medium"
                                            title={input.files.map((file) => file.name).join(", ")}
                                        >
                                            {input.files.length === 1
                                                ? input.files[0].name
                                                : `${input.files.length} files`}
                                        </span>
                                        <span
                                            role="status"
                                            className="relative max-w-[45%] text-right text-sm text-[#2b2947]/60 sm:text-base"
                                        >
                                            {status}
                                        </span>
                                        <button
                                            type="button"
                                            aria-label="Remove files"
                                            disabled={input.submitting}
                                            onClick={resetFile}
                                            className="relative flex-none rounded-full text-[#aaa0b1] focus-visible:outline focus-visible:outline-[#cf02c6] disabled:opacity-40"
                                        >
                                            <IconCircleX size={28} stroke={1.7} />
                                        </button>
                                    </div>
                                    {totalSize > 5_000_000_000 && (
                                        <div className="mt-3 flex gap-2 rounded-[12px] bg-[#fff6df] px-5 py-3 text-base font-medium leading-[1.4] text-[#c69d35]">
                                            <IconInfoCircle size={22} className="mt-0.5 flex-none" aria-hidden="true" />
                                            <p>
                                                Your upload is {(totalSize / 1_000_000_000).toFixed(1)}GB. The
                                                processing can take more than 10 minutes.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {input.invited ? (
                                    <div className="space-y-6 px-5 py-6">
                                        <h3 className="text-2xl font-medium tracking-tight lg:text-3xl">
                                            Check your email
                                        </h3>
                                        <p className="break-words text-[#625e76]">
                                            We’ve sent an invitation to&nbsp;<b>{input.email}</b>. Open it to view and
                                            share your project.
                                        </p>
                                    </div>
                                ) : input.submitting ? (
                                    <div role="status" className="space-y-6 px-5 py-6">
                                        <h3 className="text-2xl font-medium tracking-tight lg:text-3xl">
                                            Preparing your project
                                        </h3>
                                        <p className="break-words text-[#625e76]">
                                            We’ll email an invitation to&nbsp;<b>{input.email}</b>&nbsp;when your data
                                            is ready.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="space-y-6 px-5 pt-3">
                                            <div>
                                                <label
                                                    htmlFor="upload-projection"
                                                    className="mb-2 block text-base text-[#aaaaaa]"
                                                >
                                                    Input projection
                                                    {!input.projectionEdited && input.proj ? " (Auto-detected)" : ""}
                                                </label>
                                                <div className="flex min-w-0 items-center gap-3 border-b border-[#e3e3e3] focus-within:border-[#cf02c6]">
                                                    <input
                                                        id="upload-projection"
                                                        required
                                                        autoComplete="off"
                                                        placeholder="EPSG code or proj4 string"
                                                        value={input.proj}
                                                        disabled={input.submitting || !!input.versionId}
                                                        onChange={(event) => {
                                                            state.projectionEdited =
                                                                event.currentTarget.value.trim() !== "";
                                                            state.proj = event.currentTarget.value;
                                                        }}
                                                        onBlur={() => {
                                                            state.proj = normalizeInputProjection(state.proj);
                                                        }}
                                                        className="min-w-0 flex-1 bg-transparent py-3 text-lg outline-none placeholder:text-[#aaa0b1]"
                                                    />
                                                    {projectionName && (
                                                        <span
                                                            className="max-w-[55%] truncate text-base text-[#aaaaaa]"
                                                            title={projectionName}
                                                        >
                                                            {projectionName}
                                                        </span>
                                                    )}
                                                </div>
                                                {input.uploads.some(
                                                    (upload) => upload.inspection?.kind === "inspecting",
                                                ) && (
                                                    <p role="status" className="mt-2 text-sm text-[#625e76]">
                                                        Detecting input projection…
                                                    </p>
                                                )}
                                                {mixedInspections(input.uploads) && (
                                                    <p role="status" className="mt-2 text-sm text-[#625e76]">
                                                        No single CRS detected for all files. The input projection will
                                                        apply to all files.
                                                    </p>
                                                )}
                                                {(submitAttempted || input.proj) && !validProjection && (
                                                    <p className="mt-2 text-sm text-red">
                                                        {input.proj
                                                            ? "Enter an EPSG code or a proj4 string."
                                                            : "Enter the input projection."}
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label
                                                    htmlFor="upload-email"
                                                    className="block text-base text-[#aaaaaa]"
                                                >
                                                    Your email
                                                </label>
                                                <input
                                                    id="upload-email"
                                                    type="email"
                                                    autoComplete="email"
                                                    required
                                                    value={input.email}
                                                    disabled={input.submitting}
                                                    aria-invalid={(emailTouched || submitAttempted) && !validEmail}
                                                    aria-describedby={
                                                        (emailTouched || submitAttempted) && !validEmail
                                                            ? "upload-email-error"
                                                            : undefined
                                                    }
                                                    onBlur={() => setEmailTouched(true)}
                                                    onChange={(event) => {
                                                        state.email = event.currentTarget.value;
                                                        setValidEmail(event.currentTarget.validity.valid);
                                                    }}
                                                    className="mt-2 w-full border-b border-[#e3e3e3] bg-transparent py-3 text-lg outline-none focus:border-[#cf02c6]"
                                                />
                                                {(emailTouched || submitAttempted) && !validEmail && (
                                                    <p id="upload-email-error" className="mt-2 text-sm text-red">
                                                        {input.email
                                                            ? "Enter a valid email address."
                                                            : "Enter your email."}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={input.submitting}
                                            className={`${buttonClass} self-start`}
                                        >
                                            {input.submitting
                                                ? "Processing…"
                                                : input.versionId || input.imported
                                                  ? "Retry"
                                                  : "Get a link"}
                                        </button>
                                    </>
                                )}
                                {input.error && (
                                    <p role="alert" className="rounded-xl bg-red/5 px-4 py-3 text-sm text-red">
                                        {input.error}
                                    </p>
                                )}
                            </form>
                        ) : (
                            <div className="flex min-h-[360px] flex-col items-center justify-center px-3 py-8 text-center lg:min-h-[500px]">
                                <IconCirclePlus
                                    size={52}
                                    stroke={1.3}
                                    className="mb-4 text-[#aaa0b1]"
                                    aria-hidden="true"
                                />
                                <h2
                                    id="upload-heading"
                                    className="text-2xl font-medium tracking-[-0.025em] lg:text-[28px]"
                                >
                                    Add your point cloud files
                                </h2>
                                <p className="mt-3 text-[13px] font-medium leading-[1.4] text-[#2b2947]/80">
                                    Free access for 3 days, accepted formats are .laz, .las, .e57
                                    <br />
                                    Files larger than 5GB will have a longer processing time
                                </p>
                                <button
                                    type="button"
                                    onClick={() => fileInput.current?.click()}
                                    className={`${buttonClass} mt-12`}
                                >
                                    Upload files
                                </button>
                                <p className="mt-3 text-[13px] font-medium text-[#2b2947]/80">or drop here</p>
                                {input.error && (
                                    <p role="alert" className="mt-4 text-sm text-red">
                                        {input.error}
                                    </p>
                                )}
                            </div>
                        )}
                        <div
                            role="img"
                            aria-label={
                                bounds.length
                                    ? "Map preview of the point cloud bounds"
                                    : "Map preview centered on Europe"
                            }
                            className="min-h-[300px] overflow-hidden rounded-[12px] sm:min-h-[400px] lg:min-h-[500px]"
                        >
                            <MapPreview
                                key={input.files.length ? "file" : "overview"}
                                bounds={bounds}
                                request={crsRequest}
                                center={previewCenter}
                                zoom={2.4}
                                interactive={false}
                                className="h-full min-h-[300px] w-full sm:min-h-[400px] lg:min-h-[500px] [&>div]:absolute [&>div]:inset-0"
                            />
                        </div>
                    </section>

                    <section aria-labelledby="trusted-heading" className="mt-20 sm:mt-28">
                        <h2
                            id="trusted-heading"
                            className="text-center text-3xl font-medium leading-tight tracking-[-0.02em] sm:text-4xl lg:text-[36px]"
                        >
                            Trusted by the world&apos;s infrastructure leaders
                        </h2>
                        <div className="mt-10 grid gap-5 lg:grid-cols-[1fr_2.06fr_1fr]">
                            <div className="flex min-h-48 items-center justify-center rounded-lg bg-[#f5f4f9] p-8 lg:min-h-[340px]">
                                <img
                                    crossOrigin="anonymous"
                                    src="https://lumidb-public.s3.eu-west-1.amazonaws.com/images/helsinki.avif"
                                    alt="City of Helsinki"
                                    className="w-full max-w-[175px]"
                                />
                            </div>
                            <figure className="flex min-h-[340px] flex-col justify-between gap-12 rounded-lg bg-[#fdcdfd] p-7 sm:p-8">
                                <blockquote className="text-lg leading-[1.4] text-[#1d1d26] sm:text-xl">
                                    We can now stream heavy datasets covering large areas. And it loads instantly, while
                                    other tools take 15 minutes just to open a file.
                                </blockquote>
                                <figcaption className="flex items-center gap-3 text-sm text-[#1d1d26]">
                                    <img
                                        crossOrigin="anonymous"
                                        src="https://lumidb-public.s3.eu-west-1.amazonaws.com/images/suvi.avif"
                                        alt="Suvi Uotila"
                                        className="h-14 w-14 flex-none rounded-full object-cover"
                                    />
                                    <div>
                                        <div>Suvi Uotila</div>
                                        <div>Team Leader, City of Helsinki</div>
                                    </div>
                                </figcaption>
                            </figure>
                            <div className="flex min-h-48 items-center justify-center rounded-lg bg-[#f5f4f9] p-8 lg:min-h-[340px]">
                                <img
                                    src="https://lumidb-public.s3.eu-west-1.amazonaws.com/images/infrakit.avif"
                                    crossOrigin="anonymous"
                                    alt="Infrakit"
                                    className="w-full max-w-[215px]"
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </main>
            <footer className="bg-[#2b2947] px-5 py-8 text-center text-sm text-white">© 2026 LumiDB</footer>
        </>
    );
}

export function Upload() {
    const key = getEnvVar("VITE_CLERK_PUBLISHABLE_KEY");
    if (!key) {
        return (
            <UploadPage
                createSession={() => Promise.reject(new Error("Anonymous uploads are not configured on this server."))}
            />
        );
    }
    return (
        <ClerkProvider telemetry={false} publishableKey={key}>
            <Session>{(createSession) => <UploadPage createSession={createSession} />}</Session>
        </ClerkProvider>
    );
}
